import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, Download, Filter, RefreshCw, SearchCheck,
  ShieldCheck, Sparkles, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import adminService from '../services/adminService';
import { formatCurrency } from '../utils/formatters';

const TYPES = [
  'MissingLocally', 'MissingInPayMongo', 'AmountMismatch', 'StatusMismatch',
  'MissingPaymentId', 'LatePayment', 'DuplicateReference', 'ModeMismatch',
];
const STATUSES = ['Open', 'AutoRepaired', 'Resolved', 'Ignored'];

function localDate(date) {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function initialRange() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 30);
  return { startDate: localDate(start), endDate: localDate(end) };
}

function words(value) {
  return String(value || '').replace(/([a-z])([A-Z])/g, '$1 $2');
}

function dateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-PH', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function statusStyle(status) {
  if (status === 'Open') return 'bg-red-50 text-red-700';
  if (status === 'AutoRepaired') return 'bg-cyan-50 text-cyan-700';
  if (status === 'Resolved') return 'bg-emerald-50 text-emerald-700';
  if (status === 'Ignored') return 'bg-slate-100 text-slate-600';
  if (status === 'Completed') return 'bg-emerald-50 text-emerald-700';
  if (status === 'Failed') return 'bg-red-50 text-red-700';
  return 'bg-amber-50 text-amber-700';
}

export default function PaymentReconciliation() {
  const [filters, setFilters] = useState(() => ({ ...initialRange(), exceptionType: '', resolutionStatus: '' }));
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [report, setReport] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [resolution, setResolution] = useState({ resolutionStatus: 'Resolved', notes: '' });
  const [saving, setSaving] = useState(false);

  const pageCount = Math.max(1, Math.ceil((report?.totalExceptions || 0) / (report?.pageSize || 25)));

  async function load(nextFilters = appliedFilters, nextPage = page) {
    setLoading(true);
    try {
      setReport(await adminService.getPaymentReconciliation({ ...nextFilters, page: nextPage, pageSize: 25 }));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load payment reconciliation');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(appliedFilters, page); }, [appliedFilters, page]);

  async function runNow() {
    setRunning(true);
    try {
      const run = await adminService.runPaymentReconciliation(48);
      toast.success(`Reconciliation completed: ${run.exceptionCount} exception${run.exceptionCount === 1 ? '' : 's'}, ${run.autoRepairedCount} repaired`);
      await load(appliedFilters, 1);
      setPage(1);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Payment reconciliation failed');
    } finally {
      setRunning(false);
    }
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const blob = await adminService.exportPaymentReconciliation(appliedFilters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payment-reconciliation-${appliedFilters.startDate}-to-${appliedFilters.endDate}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not export reconciliation results');
    } finally {
      setExporting(false);
    }
  }

  function openResolution(item) {
    setSelected(item);
    setResolution({ resolutionStatus: 'Resolved', notes: '' });
  }

  async function saveResolution(event) {
    event.preventDefault();
    if (!resolution.notes.trim()) {
      toast.error('Add a resolution note for the audit trail');
      return;
    }
    setSaving(true);
    try {
      await adminService.resolvePaymentReconciliationException(selected.id, resolution);
      toast.success(`Exception marked as ${resolution.resolutionStatus.toLowerCase()}`);
      setSelected(null);
      await load(appliedFilters, page);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not save the resolution');
    } finally {
      setSaving(false);
    }
  }

  const latestRun = report?.recentRuns?.[0];
  const cards = useMemo(() => [
    { label: 'Open exceptions', value: report?.openExceptions ?? 0, icon: AlertTriangle, color: 'bg-red-50 text-red-700' },
    { label: 'Auto-repaired', value: report?.autoRepairedExceptions ?? 0, icon: Sparkles, color: 'bg-cyan-50 text-cyan-700' },
    { label: 'Resolved / ignored', value: report?.resolvedExceptions ?? 0, icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-700' },
    { label: 'Latest difference', value: formatCurrency(latestRun?.difference || 0), icon: ShieldCheck, color: 'bg-violet-50 text-violet-700' },
  ], [report, latestRun]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Payment Reconciliation</h1>
          <p className="mt-1 max-w-2xl text-sm text-[#667085]">Compare FREAC payments with PayMongo, repair safe status gaps, and track every exception to resolution.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={exportCsv} disabled={exporting} className="admin-btn-secondary inline-flex h-10 items-center gap-2 px-4 disabled:opacity-50">
            <Download size={16} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button onClick={runNow} disabled={running} className="admin-btn-primary inline-flex h-10 items-center gap-2 px-4 disabled:opacity-60">
            <RefreshCw size={16} className={running ? 'animate-spin' : ''} /> {running ? 'Reconciling…' : 'Reconcile now'}
          </button>
        </div>
      </div>

      <section className="admin-card">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1F2937]"><Filter size={17} className="text-[#0B7F83]" /> Exception filters</div>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
          <label className="text-xs font-medium text-[#475467]">From
            <input type="date" value={filters.startDate} max={filters.endDate} onChange={(event) => setFilters((current) => ({ ...current, startDate: event.target.value }))} className="admin-input mt-1 h-10 w-full" />
          </label>
          <label className="text-xs font-medium text-[#475467]">To
            <input type="date" value={filters.endDate} min={filters.startDate} onChange={(event) => setFilters((current) => ({ ...current, endDate: event.target.value }))} className="admin-input mt-1 h-10 w-full" />
          </label>
          <label className="text-xs font-medium text-[#475467]">Exception type
            <select value={filters.exceptionType} onChange={(event) => setFilters((current) => ({ ...current, exceptionType: event.target.value }))} className="admin-input mt-1 h-10 w-full">
              <option value="">All types</option>{TYPES.map((type) => <option key={type} value={type}>{words(type)}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-[#475467]">Resolution
            <select value={filters.resolutionStatus} onChange={(event) => setFilters((current) => ({ ...current, resolutionStatus: event.target.value }))} className="admin-input mt-1 h-10 w-full">
              <option value="">All statuses</option>{STATUSES.map((status) => <option key={status} value={status}>{words(status)}</option>)}
            </select>
          </label>
          <button onClick={() => { setPage(1); setAppliedFilters(filters); }} className="admin-btn-primary h-10 self-end px-4">Apply filters</button>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => <div key={card.label} className="admin-card flex items-center gap-4"><div className={`flex h-11 w-11 items-center justify-center rounded-xl ${card.color}`}><card.icon size={21} /></div><div><p className="text-xs text-[#667085]">{card.label}</p><p className="mt-1 text-xl font-bold text-[#1F2937]">{card.value}</p></div></div>)}
      </div>

      <section className="admin-card">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div><h2 className="font-semibold text-[#1F2937]">Reconciliation exceptions</h2><p className="text-xs text-[#667085]">Only successful provider results may be auto-repaired. Amount, duplicate, late, and missing-record issues require review.</p></div>
          <SearchCheck size={20} className="shrink-0 text-[#0B7F83]" />
        </div>
        {loading ? <div className="flex justify-center py-14"><div className="h-8 w-8 animate-spin rounded-full border-4 border-admin-600 border-t-transparent" /></div> : (
          <div className="overflow-x-auto">
            <table className="admin-table min-w-[1320px]"><thead><tr><th>Last seen</th><th>Exception</th><th>Resolution</th><th>Booking / Payment</th><th>PayMongo references</th><th>Amounts</th><th>Statuses</th><th>Details</th><th></th></tr></thead>
              <tbody>{report?.exceptions?.length ? report.exceptions.map((item) => <tr key={item.id}>
                <td className="whitespace-nowrap text-xs text-[#475467]">{dateTime(item.lastSeenAt)}<p className="mt-1 text-[11px] text-[#98A2B3]">First: {dateTime(item.firstSeenAt)}</p></td>
                <td><span className="rounded-full bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700">{words(item.exceptionType)}</span></td>
                <td><span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusStyle(item.resolutionStatus)}`}>{words(item.resolutionStatus)}</span>{item.resolutionNotes && <p className="mt-2 max-w-[180px] text-xs text-[#667085]">{item.resolutionNotes}</p>}</td>
                <td className="font-mono text-xs text-[#667085]"><p title={item.bookingId || ''}>Booking: {item.bookingId ? item.bookingId.slice(0, 8) : '—'}</p><p className="mt-1" title={item.paymentId || ''}>Payment: {item.paymentId ? item.paymentId.slice(0, 8) : '—'}</p></td>
                <td className="max-w-[230px] font-mono text-xs text-[#667085]"><p className="truncate" title={item.payMongoPaymentId || ''}>{item.payMongoPaymentId || 'No payment ID'}</p><p className="mt-1 truncate" title={item.paymentIntentId || ''}>{item.paymentIntentId || 'No intent ID'}</p></td>
                <td className="whitespace-nowrap text-xs"><p>FREAC: <strong>{item.localAmount == null ? '—' : formatCurrency(item.localAmount)}</strong></p><p className="mt-1">PayMongo: <strong>{item.providerAmount == null ? '—' : formatCurrency(item.providerAmount)}</strong></p></td>
                <td className="whitespace-nowrap text-xs text-[#475467]"><p>FREAC: {item.localStatus || '—'}</p><p className="mt-1">PayMongo: {item.providerStatus || '—'}</p></td>
                <td className="max-w-[280px] text-xs leading-5 text-[#475467]">{item.details}</td>
                <td>{item.resolutionStatus === 'Open' && <button onClick={() => openResolution(item)} className="admin-btn-secondary whitespace-nowrap px-3 py-2 text-xs">Review</button>}</td>
              </tr>) : <tr><td colSpan="9" className="py-14 text-center"><CheckCircle2 className="mx-auto mb-3 text-emerald-500" size={32} /><p className="font-medium text-[#344054]">No reconciliation exceptions match these filters.</p></td></tr>}</tbody>
            </table>
          </div>
        )}
        <div className="mt-4 flex items-center justify-between border-t border-[#EAECF0] pt-4 text-sm text-[#667085]"><span>Page {page} of {pageCount}</span><div className="flex gap-2"><button disabled={page <= 1} onClick={() => setPage((current) => current - 1)} className="admin-btn-secondary px-3 py-1.5 disabled:opacity-40">Previous</button><button disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)} className="admin-btn-secondary px-3 py-1.5 disabled:opacity-40">Next</button></div></div>
      </section>

      <section className="admin-card">
        <div className="mb-4"><h2 className="font-semibold text-[#1F2937]">Recent runs</h2><p className="text-xs text-[#667085]">Hourly scheduled and manually started comparisons.</p></div>
        <div className="overflow-x-auto"><table className="admin-table min-w-[1050px]"><thead><tr><th>Started</th><th>Status</th><th>Window</th><th>PayMongo</th><th>FREAC</th><th>Matched</th><th>Repaired</th><th>Exceptions</th><th>Difference</th></tr></thead><tbody>
          {report?.recentRuns?.length ? report.recentRuns.map((run) => <tr key={run.id}><td className="whitespace-nowrap">{dateTime(run.startedAt)}</td><td><span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusStyle(run.status)}`}>{run.status}</span>{!run.providerWindowComplete && run.status === 'Completed' && <p className="mt-1 text-[11px] text-amber-700">Partial provider window</p>}{run.errorMessage && <p className="mt-1 max-w-[220px] text-xs text-red-600">{run.errorMessage}</p>}</td><td className="whitespace-nowrap text-xs text-[#667085]">{dateTime(run.windowStart)}<br />to {dateTime(run.windowEnd)}</td><td>{run.providerPaymentCount}<p className="text-xs text-[#667085]">{formatCurrency(run.providerTotal)}</p></td><td>{run.localPaymentCount}<p className="text-xs text-[#667085]">{formatCurrency(run.localTotal)}</p></td><td>{run.matchedCount}</td><td className="font-semibold text-cyan-700">{run.autoRepairedCount}</td><td className={run.exceptionCount ? 'font-semibold text-red-700' : ''}>{run.exceptionCount}</td><td className={`font-semibold ${Math.abs(run.difference) > 0.009 ? 'text-red-700' : 'text-emerald-700'}`}>{formatCurrency(run.difference)}</td></tr>) : <tr><td colSpan="9" className="py-10 text-center text-sm text-[#667085]">No reconciliation run has been recorded yet.</td></tr>}
        </tbody></table></div>
      </section>

      {selected && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#101828]/50 p-4 backdrop-blur-sm" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelected(null); }}>
        <form onSubmit={saveResolution} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4"><div><h2 className="text-lg font-bold text-[#1F2937]">Resolve reconciliation exception</h2><p className="mt-1 text-sm text-[#667085]">Your decision and notes are retained in the audit trail.</p></div><button type="button" onClick={() => setSelected(null)} className="rounded-lg p-1.5 text-[#667085] hover:bg-[#F2F4F7]"><X size={19} /></button></div>
          <div className="mt-5 rounded-xl border border-[#EAECF0] bg-[#F9FAFB] p-4"><p className="text-sm font-semibold text-[#344054]">{words(selected.exceptionType)}</p><p className="mt-1 text-xs leading-5 text-[#667085]">{selected.details}</p></div>
          <label className="mt-5 block text-sm font-medium text-[#344054]">Decision<select value={resolution.resolutionStatus} onChange={(event) => setResolution((current) => ({ ...current, resolutionStatus: event.target.value }))} className="admin-input mt-1.5 h-11 w-full"><option value="Resolved">Resolved</option><option value="Ignored">Ignored / accepted difference</option></select></label>
          <label className="mt-4 block text-sm font-medium text-[#344054]">Resolution notes<textarea value={resolution.notes} onChange={(event) => setResolution((current) => ({ ...current, notes: event.target.value }))} rows="4" maxLength="2000" placeholder="Describe what was checked and why this exception can be closed…" className="admin-input mt-1.5 w-full resize-none" /></label>
          <div className="mt-6 flex justify-end gap-2"><button type="button" onClick={() => setSelected(null)} className="admin-btn-secondary px-4 py-2">Cancel</button><button type="submit" disabled={saving} className="admin-btn-primary px-4 py-2 disabled:opacity-60">{saving ? 'Saving…' : 'Save resolution'}</button></div>
        </form>
      </div>}
    </div>
  );
}
